import {
  BaseModel,
  useInScopeModel,
} from '@cotera/client/v0/actions/framework';
import {
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework/watchable';
import type { QueryClient } from '@tanstack/react-query';
import type { UrlParamAdapter } from '@cotera/client/app/etc/url-param-watchable';
import { MultiKeyUrlParamWatchable } from '@cotera/client/app/etc/url-param-watchable';
import {
  RESOURCE_LIST_FETCH_STALE_MS,
  queryKeys,
} from '@cotera/client/app/hooks/query-cache-keys';
import { ResourceType } from '@cotera/api';
import type { ChatTriggerSource } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { Assert } from '@cotera/utilities';
import type { StoredMessage } from '@cotera/api';
import { WatchablesFactory } from '../../watchables/watchables-factory';
import { type SidebarFolderScope } from '../../shell/sidebar/coco-sidebar-folder-tree';
import {
  DEFAULT_CHAT_TRIGGER_GROUPS,
  isChatTriggerGroup,
  resolveAgentTriggerSources,
  triggerSourcesForGroups,
  type ChatTriggerGroup,
} from '../utils/chat-trigger-source';

const FOLDER_ID_PARAM = 'folderId';
const SCOPE_PARAM = 'scope';
const UNFILED_PARAM = 'unfiled';
const UNREAD_PARAM = 'unread';
const TRIGGERS_PARAM = 'triggers';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function parseFolderId(params: URLSearchParams): string | undefined {
  const raw = params.get(FOLDER_ID_PARAM);
  if (raw === null || raw === '') {
    return undefined;
  }
  return UUID_RE.test(raw) ? raw : undefined;
}

function parseSidebarScope(
  params: URLSearchParams
): SidebarFolderScope | undefined {
  const raw = params.get(SCOPE_PARAM);
  if (raw === 'private' || raw === 'shared') {
    return raw;
  }
  return undefined;
}

function parseUnfiled(params: URLSearchParams): boolean {
  const v = params.get(UNFILED_PARAM);
  return v === '1' || v === 'true';
}

function parseUnreadOnly(params: URLSearchParams): boolean {
  const v = params.get(UNREAD_PARAM);
  return v === '1' || v === 'true';
}

/** Reads `?triggers=chat,workflow`; unknown or missing values fall back to the default. */
function parseTriggerGroups(params: URLSearchParams): ChatTriggerGroup[] {
  const raw = params.get(TRIGGERS_PARAM);
  if (raw === null || raw === '') {
    return DEFAULT_CHAT_TRIGGER_GROUPS;
  }
  const groups = raw.split(',').filter(isChatTriggerGroup);
  return groups.length > 0 ? groups : DEFAULT_CHAT_TRIGGER_GROUPS;
}

function triggerGroupsEqual(
  a: ChatTriggerGroup[],
  b: ChatTriggerGroup[]
): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

export type ListParamsState = {
  page: number;
  limit: number;
  search: string;
  sort: string | undefined;
  sortDir: 'asc' | 'desc';
  /** List chats in this folder only (folderId wins over unfiled scope). */
  folderId: string | undefined;
  /** Sidebar scope for unfiled-only view. */
  scope: SidebarFolderScope | undefined;
  /** When true with scope, only unfiled chats in that scope. */
  unfiled: boolean;
  /** When true, only show chats with unread assistant messages. */
  unreadOnly: boolean;
  /** Agent chats tab: when non-empty, only include these trigger sources. */
  triggerSources: ChatTriggerSource[];
  /**
   * Chat history list: which coarse trigger groups to show. Always non-empty;
   * defaults to user-initiated chats only.
   */
  triggerGroups: ChatTriggerGroup[];
};

export const DEFAULT_CHAT_LIST_PARAMS: ListParamsState = {
  page: 1,
  limit: 50,
  search: '',
  sort: undefined,
  sortDir: 'asc',
  folderId: undefined,
  scope: undefined,
  unfiled: false,
  unreadOnly: false,
  triggerSources: [],
  triggerGroups: DEFAULT_CHAT_TRIGGER_GROUPS,
};

function parsePage(s: string): number {
  if (s === '' || s === null || s === undefined) {
    return DEFAULT_CHAT_LIST_PARAMS.page;
  }
  const n = Number(s);
  return Number.isFinite(n) && n >= 1
    ? Math.floor(n)
    : DEFAULT_CHAT_LIST_PARAMS.page;
}

function parseLimit(s: string): number {
  if (s === '' || s === null || s === undefined) {
    return DEFAULT_CHAT_LIST_PARAMS.limit;
  }
  const n = Number(s);
  return Number.isFinite(n)
    ? Math.min(100, Math.max(1, Math.floor(n)))
    : DEFAULT_CHAT_LIST_PARAMS.limit;
}

function parseListParams(params: URLSearchParams): ListParamsState {
  return {
    page: parsePage(params.get('page') ?? ''),
    limit: parseLimit(params.get('limit') ?? ''),
    search: params.get('search') ?? DEFAULT_CHAT_LIST_PARAMS.search,
    sort: (() => {
      const v = params.get('sort');
      return v !== null && v !== '' ? v : undefined;
    })(),
    sortDir:
      params.get('sortDir') === 'desc'
        ? 'desc'
        : DEFAULT_CHAT_LIST_PARAMS.sortDir,
    folderId: parseFolderId(params),
    scope: parseSidebarScope(params),
    unfiled: parseUnfiled(params),
    unreadOnly: parseUnreadOnly(params),
    triggerSources: [],
    triggerGroups: parseTriggerGroups(params),
  };
}

function serializeListParams(s: ListParamsState): Record<string, string> {
  return {
    page: s.page === DEFAULT_CHAT_LIST_PARAMS.page ? '' : String(s.page),
    limit: String(s.limit),
    search: s.search,
    sort: s.sort ?? '',
    sortDir: s.sortDir === 'asc' ? '' : s.sortDir,
    folderId: s.folderId ?? '',
    scope: s.scope ?? '',
    unfiled: s.unfiled === true ? '1' : '',
    unread: s.unreadOnly === true ? '1' : '',
    [TRIGGERS_PARAM]: serializeTriggerGroups(s.triggerGroups),
  };
}

/** The default selection is left out of the URL entirely. */
function serializeTriggerGroups(groups: ChatTriggerGroup[]): string {
  return triggerGroupsEqual(groups, DEFAULT_CHAT_TRIGGER_GROUPS)
    ? ''
    : groups.join(',');
}

function listParamsEquality(a: ListParamsState, b: ListParamsState): boolean {
  return (
    a.page === b.page &&
    a.limit === b.limit &&
    a.search === b.search &&
    a.sort === b.sort &&
    a.sortDir === b.sortDir &&
    a.folderId === b.folderId &&
    a.scope === b.scope &&
    a.unfiled === b.unfiled &&
    a.unreadOnly === b.unreadOnly &&
    a.triggerSources.length === b.triggerSources.length &&
    a.triggerSources.every(
      (value, index) => value === b.triggerSources[index]
    ) &&
    triggerGroupsEqual(a.triggerGroups, b.triggerGroups)
  );
}

export type ConversationListItem = {
  id: string;
  /** Chat owner (the actor who created it); used for "Owned by" on inbound shares. */
  actorId: string;
  title: string | null;
  lastMessage: StoredMessage | null;
  firstMessage: StoredMessage | null;
  updatedAt: Date;
  folderName: string | null;
  folderScope: 'user' | 'org' | 'external' | null;
  hasUnreadAssistant: boolean;
  triggerSource: ChatTriggerSource | null;
};

export type ChatHistoryListData = {
  items: ConversationListItem[];
  total: number;
};

export type ChatHistoryListPageModelProps = {
  orgId: string;
  queryClient: QueryClient;
  watchablesFactory: WatchablesFactory;
  urlAdapter: UrlParamAdapter;
  initialSearchParams: URLSearchParams;
  client: TenantedClient;
  /** When set, only list chats linked to this saved agent. */
  agentId?: string;
};

type ChatListQueryDeps = {
  orgId: string;
  queryClient: QueryClient;
  client: TenantedClient;
};

function toConversationListItem(chat: {
  id: string;
  actorId: string;
  title: string;
  updatedAt: Date;
  lastMessage?: StoredMessage | null;
  firstMessage?: StoredMessage | null;
  folderName?: string | null;
  folderScope?: 'user' | 'org' | 'external' | null;
  hasUnreadAssistant?: boolean;
  triggerSource?: ChatTriggerSource | null;
}): ConversationListItem {
  return {
    id: chat.id,
    actorId: chat.actorId,
    title: chat.title,
    lastMessage: chat.lastMessage ?? null,
    firstMessage: chat.firstMessage ?? null,
    updatedAt: chat.updatedAt,
    folderName: chat.folderName ?? null,
    folderScope: chat.folderScope ?? null,
    hasUnreadAssistant: chat.hasUnreadAssistant === true,
    triggerSource: chat.triggerSource ?? 'chat',
  };
}

function chatListDataEquality(
  a: ChatHistoryListData | null,
  b: ChatHistoryListData | null
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  if (a.total !== b.total || a.items.length !== b.items.length) {
    return false;
  }
  for (let i = 0; i < a.items.length; i++) {
    if (
      a.items[i]?.id !== b.items[i]?.id ||
      a.items[i]?.folderName !== b.items[i]?.folderName ||
      a.items[i]?.folderScope !== b.items[i]?.folderScope
    ) {
      return false;
    }
  }
  return true;
}

async function fetchChatsInFolder(
  deps: ChatListQueryDeps,
  params: {
    folderId: string;
    page: number;
    limit: number;
    search: string;
    sort: string | undefined;
    sortDir: 'asc' | 'desc';
    unreadOnly: boolean;
    triggerSources: ChatTriggerSource[];
  }
): Promise<ChatHistoryListData> {
  const { orgId, queryClient, client } = deps;
  // `ResourceListView` infinite scroll expects the model to return a cumulative
  // list for page N (i.e. items from pages 1..N). Otherwise the UI replaces
  // earlier rows instead of appending them.
  const allItems: ConversationListItem[] = [];
  let total = 0;

  for (let p = 1; p <= params.page; p++) {
    const offset = (p - 1) * params.limit;
    const out = await queryClient.fetchQuery({
      queryKey: queryKeys.resource.folderContents({
        orgId,
        folderId: params.folderId,
        itemTypes: [ResourceType.Chat],
        ...(params.search !== '' && { search: params.search }),
        sort: params.sort ?? 'updatedAt',
        sortDir: params.sortDir,
        limit: params.limit,
        offset,
        ...(params.unreadOnly && { unreadOnly: true }),
        triggerSources: params.triggerSources,
      }),
      staleTime: RESOURCE_LIST_FETCH_STALE_MS,
      queryFn: async () =>
        Assert.assertOk(
          await client.resource.folder.getFolderItems({
            folderId: params.folderId,
            itemTypes: [ResourceType.Chat],
            ...(params.search !== '' && { search: params.search }),
            sort: params.sort ?? 'updatedAt',
            sortDir: params.sortDir,
            limit: params.limit,
            offset,
            ...(params.unreadOnly && { unreadOnly: true }),
            triggerSources: params.triggerSources,
          })
        ),
    });
    total = out.total;

    for (const item of out.items) {
      if (item.type !== ResourceType.Chat) {
        continue;
      }
      allItems.push(
        toConversationListItem({
          id: item.data.id,
          actorId: item.data.actorId,
          title: item.data.title,
          updatedAt: item.data.updatedAt,
          lastMessage: item.data.lastMessage ?? null,
          firstMessage: item.data.firstMessage ?? null,
          folderName: item.data.folderName ?? null,
          folderScope: item.data.folderScope ?? null,
          hasUnreadAssistant: item.data.hasUnreadAssistant === true,
          triggerSource: item.data.triggerSource ?? 'chat',
        })
      );
    }

    if (out.items.length < params.limit) {
      break;
    }
  }

  return { items: allItems, total };
}

/** Exported for unit coverage of the scope-param passthrough + server-total usage. */
export async function fetchDefaultChatList(
  deps: ChatListQueryDeps,
  params: {
    page: number;
    limit: number;
    search: string;
    sort: string | undefined;
    sortDir: 'asc' | 'desc';
    unreadOnly: boolean;
    agentId?: string;
    triggerSources?: ChatTriggerSource[];
    /** Sidebar Unfiled sections: server-side scope filter (replaces the old scan). */
    unfiledOnly?: boolean;
    folderScope?: SidebarFolderScope;
  }
): Promise<ChatHistoryListData> {
  const { orgId, queryClient, client } = deps;
  const allItems: ConversationListItem[] = [];
  let total = 0;
  for (let p = 1; p <= params.page; p++) {
    const offset = (p - 1) * params.limit;
    const scopeParams = {
      ...(params.unfiledOnly === true && { unfiledOnly: true }),
      ...(params.folderScope !== undefined && {
        folderScope: params.folderScope,
      }),
    };
    const out = await queryClient.fetchQuery({
      queryKey: queryKeys.resource.listPage({
        orgId,
        type: ResourceType.Chat,
        limit: params.limit,
        offset,
        ...(params.search !== undefined &&
          params.search !== '' && { search: params.search }),
        ...(params.sort !== undefined &&
          params.sort !== '' && { sort: params.sort, sortDir: params.sortDir }),
        ...(params.unreadOnly && { unreadOnly: true }),
        ...(params.agentId !== undefined && { agentId: params.agentId }),
        ...(params.triggerSources !== undefined &&
          params.triggerSources.length > 0 && {
            triggerSources: params.triggerSources,
          }),
        ...scopeParams,
      }),
      staleTime: RESOURCE_LIST_FETCH_STALE_MS,
      queryFn: async () =>
        Assert.assertOk(
          await client.resource.list({
            type: ResourceType.Chat,
            limit: params.limit,
            offset,
            ...(params.search !== undefined &&
              params.search !== '' && { search: params.search }),
            ...(params.sort !== undefined &&
              params.sort !== '' && {
                sort: params.sort,
                sortDir: params.sortDir,
              }),
            ...(params.unreadOnly && { unreadOnly: true }),
            ...(params.agentId !== undefined && { agentId: params.agentId }),
            ...(params.triggerSources !== undefined &&
              params.triggerSources.length > 0 && {
                triggerSources: params.triggerSources,
              }),
            ...scopeParams,
          })
        ),
    });
    if (out.type !== 'chat') {
      break;
    }
    total = out.total;
    allItems.push(
      ...out.items.map((chat) =>
        toConversationListItem({
          id: chat.id,
          actorId: chat.actorId,
          title: chat.title,
          updatedAt: chat.updatedAt,
          lastMessage: chat.lastMessage ?? null,
          firstMessage: chat.firstMessage ?? null,
          folderName: chat.folderName ?? null,
          folderScope: chat.folderScope ?? null,
          hasUnreadAssistant: chat.hasUnreadAssistant === true,
          triggerSource: chat.triggerSource ?? 'chat',
        })
      )
    );
    if (out.items.length < params.limit) {
      break;
    }
  }
  return { items: allItems, total };
}

/**
 * Whether the list can show chats the server never cascades into
 * `recent-chats-updated` — anything not started interactively. Mirrors the
 * trigger-source resolution in the fetch below.
 */
export function listsNonRecentChats(
  params: ListParamsState,
  agentId: string | undefined
): boolean {
  const sources =
    agentId !== undefined
      ? resolveAgentTriggerSources(params.triggerSources)
      : triggerSourcesForGroups(params.triggerGroups);
  return sources.some((source) => source !== 'chat');
}

/**
 * Model for the chat history list page. Owns URL-synced watchables for list params (page, limit,
 * search, sort, sortDir, folderId, scope, unfiled) and an event watchable that fetches chat list data.
 */
export class ChatHistoryListPageModel extends BaseModel {
  private readonly _listParamsUrl: MultiKeyUrlParamWatchable<ListParamsState>;
  private readonly _listParams: ReadonlyWatchable<ListParamsState>;
  private readonly _chatsDataEventWatchable: ReadonlyEventWatchable<ChatHistoryListData | null>;

  constructor(props: ChatHistoryListPageModelProps) {
    super('chat-history-list-page');
    const initial = parseListParams(props.initialSearchParams);
    const adapter = props.urlAdapter;

    this._listParamsUrl = new MultiKeyUrlParamWatchable<ListParamsState>(
      [
        'page',
        'limit',
        'search',
        'sort',
        'sortDir',
        FOLDER_ID_PARAM,
        SCOPE_PARAM,
        UNFILED_PARAM,
        UNREAD_PARAM,
        TRIGGERS_PARAM,
      ],
      {
        default: DEFAULT_CHAT_LIST_PARAMS,
        parse: parseListParams,
        serialize: serializeListParams,
        equalityFn: listParamsEquality,
      },
      adapter,
      initial
    );

    this._listParams = this._listParamsUrl.asWatchable();

    const watchablesFactory = props.watchablesFactory;

    this._chatsDataEventWatchable =
      watchablesFactory.makeChatHistoryListWatchable<
        ChatHistoryListData | null,
        unknown
      >({
        orgId: props.orgId,
        listsNonRecentChats: () =>
          listsNonRecentChats(this._listParams.snapshot(), props.agentId),
        initialValue: null,
        equalityFn: chatListDataEquality,
        fn: async (_payload, get) => {
          const {
            page,
            limit,
            search,
            sort,
            sortDir,
            folderId,
            scope,
            unfiled,
            unreadOnly,
            triggerSources,
            triggerGroups,
          } = get(this._listParams);

          const queryDeps: ChatListQueryDeps = {
            orgId: props.orgId,
            queryClient: props.queryClient,
            client: props.client,
          };

          const historyTriggerSources = triggerSourcesForGroups(triggerGroups);
          const agentTriggerSources =
            resolveAgentTriggerSources(triggerSources);

          // Agent-scoped lists ignore folder/unfiled scoping and always use the
          // default (agent-filtered) chat list.
          if (props.agentId !== undefined) {
            return fetchDefaultChatList(queryDeps, {
              page,
              limit,
              search,
              sort,
              sortDir,
              unreadOnly,
              agentId: props.agentId,
              triggerSources: agentTriggerSources,
            });
          }

          if (folderId !== undefined) {
            return fetchChatsInFolder(queryDeps, {
              folderId,
              page,
              limit,
              search,
              sort,
              sortDir,
              unreadOnly,
              triggerSources: historyTriggerSources,
            });
          }

          if (unfiled === true && scope !== undefined) {
            // Server-side scope filter: one page instead of scanning up to 2000
            // full-query pages and filtering in the browser. `total` is now the
            // true count, so `hasMore` in the page component is correct.
            return fetchDefaultChatList(queryDeps, {
              page,
              limit,
              search,
              sort,
              sortDir,
              unreadOnly,
              triggerSources: historyTriggerSources,
              unfiledOnly: true,
              folderScope: scope,
            });
          }

          return fetchDefaultChatList(queryDeps, {
            page,
            limit,
            search,
            sort,
            sortDir,
            unreadOnly,
            triggerSources: historyTriggerSources,
          });
        },
      });
  }

  get listParamsWatchable(): ReadonlyWatchable<ListParamsState> {
    return this._listParams;
  }

  get chatsDataWatchable(): ReadonlyEventWatchable<ChatHistoryListData | null> {
    return this._chatsDataEventWatchable;
  }

  /**
   * Adopt list params the URL changed out from under us — back/forward, or an
   * inbound link. Deliberately NOT the refetch path for our own setters: those
   * refresh directly (see {@link updateListParams}), so a filter change never
   * depends on a router round-trip landing an effect back here.
   *
   * Our setters write `_listParamsUrl` before navigating, so on the echo of our
   * own navigation `before` already holds the new value and this no-ops rather
   * than firing a second identical fetch.
   */
  syncFromUrl(searchParams: URLSearchParams): void {
    const before = this._listParamsUrl.snapshot();
    this._listParamsUrl.syncFromUrl(searchParams);
    const after = this._listParamsUrl.snapshot();
    if (listParamsEquality(before, after)) {
      return;
    }
    void this._chatsDataEventWatchable.refresh();
  }

  /**
   * Set list params to the given page and take. Backs "load more", which
   * appends: the fetch refetches pages 1..page and concatenates, so the rows
   * already on screen stay a prefix of the result.
   */
  loadPage(page: number, take: number): void {
    const current = this._listParamsUrl.snapshot();
    this.updateListParams({ ...current, page, limit: take });
  }

  /** Set search query; resets page to 1. */
  setSearch(search: string): void {
    const current = this._listParamsUrl.snapshot();
    this.updateListParams({ ...current, search, page: 1 });
  }

  /** Set sort key and direction; resets page to 1. */
  setSort(sort: string | undefined, sortDir: 'asc' | 'desc'): void {
    const current = this._listParamsUrl.snapshot();
    this.updateListParams({ ...current, sort, sortDir, page: 1 });
  }

  /** Toggle unread-only filter; resets page to 1. */
  setUnreadOnly(unreadOnly: boolean): void {
    const current = this._listParamsUrl.snapshot();
    this.updateListParams({ ...current, unreadOnly, page: 1 });
  }

  /** Set trigger-group filters (chat history list); resets page to 1. */
  setTriggerGroups(triggerGroups: ChatTriggerGroup[]): void {
    const current = this._listParamsUrl.snapshot();
    this.updateListParams({ ...current, triggerGroups, page: 1 });
  }

  /** Set trigger-source filters (agent chats tab); resets page to 1. */
  setTriggerSources(triggerSources: ChatTriggerSource[]): void {
    const current = this._listParamsUrl.snapshot();
    this.updateListParams({ ...current, triggerSources, page: 1 });
  }

  /**
   * Commit new list params and refetch for them, mirroring the Library.
   *
   * The refetch is fired here rather than left to `syncFromUrl`, which is what
   * every filter used to rely on: writing the URL and waiting for the router to
   * hand a new `searchParams` back to the page effect. Every one of these
   * filters serializes to `''` at its default value, and `useUrlAdapter` deletes
   * empty params — so *turning a filter off* is the case where the round-trip
   * has to survive a key being removed. When it didn't, the params were right
   * and the rows were stale until a reload.
   */
  private updateListParams(next: ListParamsState): void {
    const current = this._listParamsUrl.snapshot();
    if (listParamsEquality(current, next)) {
      return;
    }
    this._listParamsUrl.set(next);
    void this._chatsDataEventWatchable.refresh();
  }

  dispose(): void {
    this._chatsDataEventWatchable.unsubscribe();
    this._listParamsUrl.dispose();
  }
}

export const useChatHistoryListPageModel = (): ChatHistoryListPageModel => {
  const model = useInScopeModel(ChatHistoryListPageModel);
  if (model === null) {
    throw new Error('ChatHistoryListPageModel not found in scope');
  }
  return model;
};
